import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const HELCIM_SCRIPT = 'https://secure.helcim.app/helcim-pay/services/start.js';

const money = (v, ccy = 'USD') =>
  `${ccy === 'CAD' ? 'CA$' : '$'}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Pay = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | paying | paid | error | invalid
  const [error, setError] = useState('');
  const helcimReady = useRef(false);
  const checkoutTokenRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/pay/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'This payment link is invalid or has expired.');
        return r.json();
      })
      .then((d) => {
        setInfo(d);
        setState(d.paid ? 'paid' : 'ready');
      })
      .catch((e) => { setState('invalid'); setError(String(e.message || e)); });

    if (!document.querySelector(`script[src="${HELCIM_SCRIPT}"]`)) {
      const s = document.createElement('script');
      s.src = HELCIM_SCRIPT; s.async = true;
      s.onload = () => { helcimReady.current = true; };
      document.body.appendChild(s);
    } else {
      helcimReady.current = true;
    }
  }, [token]);

  useEffect(() => {
    const onMessage = async (event) => {
      const ct = checkoutTokenRef.current;
      if (!ct || !event.data || event.data.eventName !== `helcim-pay-js-${ct}`) return;
      if (event.data.eventStatus === 'ABORTED' || event.data.eventStatus === 'HIDE') {
        if (window.removeHelcimPayIframe) window.removeHelcimPayIframe();
        setState('ready');
        if (event.data.eventStatus === 'ABORTED') setError('The payment was declined or cancelled. Please try again.');
        return;
      }
      if (event.data.eventStatus !== 'SUCCESS') return;
      try {
        const res = await fetch(`${API}/api/pay/${token}/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ checkoutToken: ct, rawDataResponse: event.data.eventMessage }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'We could not confirm your payment. Please contact us.');
        if (window.removeHelcimPayIframe) window.removeHelcimPayIframe();
        setState('paid');
        setError('');
      } catch (e) {
        if (window.removeHelcimPayIframe) window.removeHelcimPayIframe();
        setState('ready');
        setError(String(e.message || e));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [token]);

  const pay = async () => {
    setError('');
    setState('paying');
    try {
      const res = await fetch(`${API}/api/pay/${token}/initialize`, { method: 'POST' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Could not start the payment.');
      const data = await res.json();
      checkoutTokenRef.current = data.checkoutToken;
      if (typeof window.appendHelcimPayIframe !== 'function') throw new Error('Payment module is still loading — please try again in a moment.');
      window.appendHelcimPayIframe(data.checkoutToken, true);
    } catch (e) {
      setState('ready');
      setError(String(e.message || e));
    }
  };

  const items = useMemo(() => info?.line_items || [], [info]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4 py-12" data-testid="pay-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <img src="/assets/fortune-logo-white.png" alt="Fortune Auto Design" className="h-10 mx-auto mb-6" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>

        {state === 'loading' && (
          <div className="flex items-center justify-center gap-2 text-neutral-400 py-20"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        )}

        {state === 'invalid' && (
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-8 text-center" data-testid="pay-invalid">
            <AlertTriangle className="mx-auto text-[#FF6B00] mb-4" size={34} />
            <h1 className="font-display text-2xl tracking-tight mb-2">LINK UNAVAILABLE</h1>
            <p className="text-neutral-400 text-sm">{error || 'This payment link is invalid or has expired.'}</p>
            <p className="text-neutral-500 text-xs mt-4">Need a new link? Email <a href="mailto:info@fortuneautodesign.com" className="text-[#FF6B00]">info@fortuneautodesign.com</a>.</p>
          </div>
        )}

        {state === 'paid' && (
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-8 text-center" data-testid="pay-success">
            <CheckCircle2 className="mx-auto text-green-500 mb-4" size={40} />
            <h1 className="font-display text-3xl tracking-tight mb-2">PAYMENT RECEIVED</h1>
            <p className="text-neutral-400 text-sm">Thank you! Your payment for <strong className="text-white">{info?.order_number}</strong> is confirmed. We&rsquo;ve emailed your receipt and your build is moving forward.</p>
          </div>
        )}

        {(state === 'ready' || state === 'paying') && info && (
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl overflow-hidden" data-testid="pay-card">
            <div className="px-7 py-5 border-b border-[#1a1a1a]">
              <div className="text-[11px] tracking-[0.3em] text-[#FF6B00] font-semibold">SECURE PAYMENT</div>
              <div className="font-display text-2xl tracking-tight mt-1">ORDER {info.order_number}</div>
            </div>
            <div className="px-7 py-5 space-y-2">
              {items.map((li, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-neutral-300">{li.name}{li.variant ? ` · ${li.variant}` : ''} <span className="text-neutral-600">× {li.quantity}</span></span>
                  <span className="text-neutral-400 tabular-nums">{money(li.total, 'USD')}</span>
                </div>
              ))}
            </div>
            <div className="px-7 py-5 border-t border-[#1a1a1a]">
              <div className="flex justify-between items-baseline">
                <span className="text-[11px] tracking-[0.22em] text-neutral-500">ORDER TOTAL</span>
                <span className="font-display text-xl tabular-nums">{money(info.usd_total, 'USD')} <span className="text-xs text-neutral-500">USD</span></span>
              </div>
              <div className="flex justify-between items-baseline mt-2 bg-[#FF6B00]/10 border border-[#FF6B00]/20 px-3 py-2.5 rounded">
                <span className="text-[10px] tracking-[0.16em] text-neutral-300">YOU&rsquo;LL BE CHARGED</span>
                <span className="font-display text-lg tabular-nums">{money(info.amount_cad, 'CAD')}</span>
              </div>
              <p className="text-[11px] text-neutral-500 mt-3">Prices shown in USD; your card is charged the equivalent in Canadian dollars (CAD) at today&rsquo;s rate.</p>

              {error && <p className="text-[13px] text-red-400 mt-3" data-testid="pay-error">{error}</p>}

              <button
                type="button"
                onClick={pay}
                disabled={state === 'paying'}
                data-testid="pay-button"
                className={`w-full mt-5 py-4 font-display tracking-[0.22em] text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors ${state === 'paying' ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed' : 'bg-[#FF6B00] hover:bg-[#E25500] text-white'}`}
              >
                {state === 'paying' ? <><Loader2 size={16} className="animate-spin" /> OPENING SECURE PAYMENT…</> : <><Lock size={15} /> PAY SECURELY</>}
              </button>
              <p className="text-[11px] text-neutral-500 text-center mt-4 inline-flex items-center justify-center gap-1.5 w-full">
                <Lock size={11} /> Card details are handled securely by Helcim. We never see or store your card number.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pay;
